import { useState, useCallback, useEffect } from 'react';
import { useMedplum } from '@medplum/react';
import { Practitioner } from '@medplum/fhirtypes';
import { useDebouncedValue } from '@mantine/hooks';

//------------------------------------------------------------------------------
export const useDoctorSearch = () => {
  const medplum = useMedplum();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [loading, setLoading] = useState(false);

  //----------------------------------------------------------------------------
  const searchPractitioners = useCallback(
    async (query: string) => {
      if (query.length < 2) {
        setPractitioners([]);
        return;
      }

      setLoading(true);
      try {
        // Search by name
        const searchResults = await medplum.searchResources('Practitioner', {
          name: query,
          _count: 10,
          _sort: 'name',
        });

        setPractitioners(searchResults);
      } catch (error) {
        console.error('Error searching practitioners:', error);
        setPractitioners([]);
      } finally {
        setLoading(false);
      }
    },
    [medplum]
  );

  //----------------------------------------------------------------------------
  useEffect(() => {
    searchPractitioners(debouncedSearch);
  }, [debouncedSearch, searchPractitioners]);

  //----------------------------------------------------------------------------
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setPractitioners([]);
  }, []);

  //----------------------------------------------------------------------------
  return {
    searchQuery,
    setSearchQuery,
    practitioners,
    loading,
    clearSearch,
  };
};
